import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import H2 from '@base/H2/H2';
import P1 from '@base/P1/P1';
import Drawer from '@base/Drawer/Drawer';
import dateHelper from '@utils/dates';
import { breakpoints, Spacings, Fonts } from '@styles/index';

const Title = styled(H2)`
  grid-column: span 10;
  margin-bottom: ${Spacings.SPACING_11B};
  color: var(--color-text-primary);
  @media only screen and (max-width: ${breakpoints.mobile}) {
    margin-bottom: ${Spacings.SPACING_6B};
  }
`;

const HighlightContainer = styled.div`
  grid-column: span 10;
  margin-bottom: ${Spacings.SPACING_11B};
  @media only screen and (max-width: ${breakpoints.mobile}) {
    margin-bottom: ${Spacings.SPACING_10B};
    & > p:last-child {
      margin-bottom: 0;
    }
  }
`;

const InfoTitle = styled(P1)`
  margin-bottom: ${Spacings.SPACING_3B};
  font-size: ${Fonts.LARGE};
  @media only screen and (max-width: ${breakpoints.mobile}) {
    font-size: 14px;
  }
`;

const Info = styled(P1)`
  margin-bottom: ${Spacings.SPACING_3B};
  font-size: ${Fonts.LARGE};
  color: var(--color-text-primary);
  @media only screen and (max-width: ${breakpoints.mobile}) {
    margin-bottom: ${Spacings.SPACING_2B};
  }
`;

const Interval = styled(P1)`
  font-size: ${Fonts.LARGE};
  display: inline;
`;

const getIntervalText = (highlight) => {
  return `${Spacings.SPACING_2SPACES}•${
    Spacings.SPACING_2SPACES
  }${dateHelper.getYear(highlight.startTime)} - ${
    highlight.currentlyRelated
      ? 'Present'
      : dateHelper.getYear(highlight.endTime)
  }`;
};
const EducatorHighlightsDrawer = ({
  showDetailedHighlights,
  onCloseHighlights,
  firstName,
  credentials,
  languages,
  knowsAbout,
  dateOfJoining
}) => {
  const workHistory = credentials?.find((item) => item.type === 1)?.value;
  const educations = credentials?.find((item) => item.type === 2)?.value;
  const accomplishments = credentials?.find((item) => item.type === 4)?.value;

  const dateOfJoiningString = dateOfJoining
    ? `${dateHelper.getDateMonthString(
        dateOfJoining,
        true,
        false
      )}, ${dateHelper.getYear(dateOfJoining)}`
    : null;
  const knowsAboutString =
    knowsAbout?.length > 0 ? knowsAbout.join(', ') : null;
  const languageString = languages?.length > 0 ? languages.join(', ') : null;
  return (
    <Drawer drawerState={showDetailedHighlights} onClose={onCloseHighlights}>
      <Title>{`${firstName}’s highlights`}</Title>
      {workHistory && workHistory.length > 0 && (
        <HighlightContainer>
          <InfoTitle color="var(--color-text-secondary)">Work</InfoTitle>
          {workHistory.map((work, index) => {
            return (
              <Info key={work.startTime ? `${work.startTime}_${index}` : index}>
                {work.title}
                <Interval color="var(--color-text-secondary)">
                  {getIntervalText(work)}
                </Interval>
              </Info>
            );
          })}
        </HighlightContainer>
      )}
      {educations && educations.length > 0 && (
        <HighlightContainer>
          <InfoTitle color="var(--color-text-secondary)">Education</InfoTitle>
          {educations.map((education, index) => {
            return (
              <Info
                key={
                  education.startTime
                    ? `${education.startTime}_${index}`
                    : index
                }
              >
                {education.title}
                <Interval color="var(--color-text-secondary)">
                  {getIntervalText(education)}
                </Interval>
              </Info>
            );
          })}
        </HighlightContainer>
      )}
      {accomplishments && accomplishments.length > 0 && (
        <HighlightContainer>
          <InfoTitle color="var(--color-text-secondary)">
            Accomplishments
          </InfoTitle>
          {accomplishments.map((accomplishment, index) => {
            return <Info key={index.toString()}>{accomplishment.title}</Info>;
          })}
        </HighlightContainer>
      )}
      {dateOfJoiningString && (
        <HighlightContainer>
          <InfoTitle color="var(--color-text-secondary)">
            Joined Unacademy on
          </InfoTitle>
          <Info>{dateOfJoiningString}</Info>
        </HighlightContainer>
      )}
      {knowsAboutString && (
        <HighlightContainer>
          <InfoTitle color="var(--color-text-secondary)">Knows about</InfoTitle>
          <Info>{knowsAboutString}</Info>
        </HighlightContainer>
      )}
      {languageString && (
        <HighlightContainer>
          <InfoTitle color="var(--color-text-secondary)">Languages</InfoTitle>
          <Info>{languageString}</Info>
        </HighlightContainer>
      )}
    </Drawer>
  );
};

EducatorHighlightsDrawer.propTypes = {
  showDetailedHighlights: PropTypes.bool.isRequired,
  onCloseHighlights: PropTypes.func.isRequired,
  firstName: PropTypes.string.isRequired,
  credentials: PropTypes.arrayOf(PropTypes.any).isRequired,
  languages: PropTypes.arrayOf(PropTypes.string).isRequired,
  knowsAbout: PropTypes.arrayOf(PropTypes.string).isRequired,
  dateOfJoining: PropTypes.string.isRequired
};

export default EducatorHighlightsDrawer;
